import { FormGroup, UntypedFormGroup } from '@angular/forms';

export class FormUtil {

  /**
   * Marks the form group as dirty, indicating that the user has modified the form.
   * This is useful when programmatically assigning values to the form.
   * @param formGroup The FormGroup to mark as dirty.
   */
  public static setFormAsDirty(formGroup: FormGroup) {
    formGroup.markAsDirty(); // Mark the form group as dirty
  }

  /**
   * Checks if a specific form control within the form group is pristine (untouched).
   * @param formGroup The FormGroup containing the control.
   * @param formControl The name of the control to check.
   * @returns True if the control is pristine, false otherwise.
   */
  public static isControlPristine(formGroup: FormGroup, formControl: string) {
    return formGroup.get(formControl)?.pristine; // Return the pristine state of the control
  }

  /**
   * Validates if a specific form control is valid only when it is not pristine.
   * If the control is pristine, it returns false.
   * @param formGroup The UntypedFormGroup containing the control.
   * @param formControl The name of the control to validate.
   * @returns True if the control is valid when not pristine, false otherwise.
   */
  public static isControlValidWhenNotPristine(formGroup: UntypedFormGroup, formControl: string) {
    // If the control is pristine, return false
    if (!FormUtil.isControlPristine(formGroup, formControl)) {
      return false;
    }

    // Check if the field is valid
    return FormUtil.isFieldValid(formGroup, formControl);
  }

  /**
   * Checks if a specific form control is valid.
   * @param formGroup The UntypedFormGroup containing the control.
   * @param formControl The name of the control to check.
   * @returns True if the control is valid, false otherwise.
   */
  public static isFieldValid(formGroup: UntypedFormGroup, formControl: string) {
    return formGroup.controls[formControl].valid; // Return the validity of the control
  }

  /**
   * Processes the form payload to trim whitespace from string values.
   * @param payload The form data payload to process.
   * @returns The processed payload with trimmed values.
   */
  public static processForm(payload: any) {
    return this.trim(payload); // Trim the payload values
  }

  /**
   * Recursively trims whitespace from string values in the payload object.
   * If the value is an object, it calls itself recursively.
   * @param payload The payload to trim.
   * @returns The trimmed payload or null if the payload is falsy.
   */
  public static trim(payload: any) {
    if (!payload) {
      return null; // Return null if the payload is falsy
    }

    // Iterate over each key in the payload
    for (const key in payload) {
      const value = payload[key];
      if (this.isObject(value)) {
        this.trim(value); // Recursively trim if the value is an object
      } else if (value) {
        // Trim the value if it's a non-empty string
        if (typeof value !== 'boolean' && typeof value !== 'number') {
          payload[key] = value ? value.trim() : null; // Trim or set to null if empty
        }
      }
    }

    return payload; // Return the trimmed payload
  }

  /**
   * Checks if the given payload is an object.
   * @param payload The payload to check.
   * @returns True if the payload is an object, false otherwise.
   */
  public static isObject(payload: any) {
    if (!payload) {
      return false; // Return false if the payload is falsy
    }

    // Check if the payload is an object
    return payload === Object(payload);
  }
}